import type { CSSProperties } from 'react';
import { shuffle } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { Controller, useForm } from 'react-hook-form';
import { Button, Field, Loader, Radio, Select, Textarea, tokens } from '@hotjar/hotsauce';

import type {
  GetSurveyResponseAnswer,
  GetSurveyResponseQuestion,
} from '@hotjar/webapp/domains/revenue/churn-survey/dto';
import { useOwnedAccount } from '@hotjar/account';
import type { DefaultChurnSurvey } from '@hotjar/webapp/domains/revenue/churn-survey/types';
import { SurveyStatus, useChurnSurvey } from '@hotjar/webapp/domains/revenue/hooks/useChurnSurvey';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { getDefaultChurnSurvey } from './churnSurveyUtils';

const S = {
  StyledBox: styled.div`
    padding: ${tokens.spacer.x4} 0 0 0;
  `,
  FormRow: styled.div`
    margin: 0 0 ${tokens.spacer.x4} 0;
    &:last-child {
      margin-bottom: 0;
    }
  `,
  Label: styled.label`
    ${tokens.text('subtitle2')}
    margin: 0 0 ${tokens.spacer.x1} 0;
    text-transform: none;
  `,
  Field: styled(Field)`
    width: 100%;
    label {
      text-transform: none;
    }
  `,
  RadioButtonContainer: styled.span`
    display: flex;
  `,
  RadioButton: styled(Radio)`
    margin: 0 ${tokens.spacer.x2} 0 0;
    text-transform: none;
  `,
  ButtonContainer: styled.div`
    text-align: right;
  `,
};

interface ChurnSurveyProps {
  onPlanChangeSubmit: () => void;
  formId?: string;
  onIsSubmittingChange?: (isSubmitting: boolean) => void;
  style?: CSSProperties;
}

interface FormResult {
  downgradeReason: string;
  downgradeExplanation: string;
  likelyToUpgrade: string;
}

export const ChurnSurvey = ({
  onPlanChangeSubmit,
  formId,
  onIsSubmittingChange,
  style,
}: ChurnSurveyProps): JSX.Element => {
  const { t } = useTranslation();
  const { getSurvey, surveyStatus, surveyQuestions, submitSurvey } = useChurnSurvey();
  const { data: account } = useOwnedAccount();
  const { formSubmit, getGroupDataForAccount, click } = useTrack();
  const [churnSurvey, setChurnSurvey] = useState<DefaultChurnSurvey | null>(null);
  const accountData = account ? getGroupDataForAccount(account) : undefined;

  const { control, register, handleSubmit, errors, formState } = useForm<FormResult>({
    mode: 'onChange',
  });
  const { isSubmitting } = formState;
  const buttonIsOutsideOfForm = formId !== undefined;

  useEffect(() => {
    if (onIsSubmittingChange !== undefined) {
      onIsSubmittingChange(isSubmitting);
    }
  }, [isSubmitting, onIsSubmittingChange]);

  const onSubmit = useCallback(
    async (formData: FormResult) => {
      if (churnSurvey) {
        const finalDataToSend = {
          ...churnSurvey,
        };

        finalDataToSend.downgradeReason.answer = formData.downgradeReason;
        finalDataToSend.downgradeExplanation.answer = formData.downgradeExplanation;
        finalDataToSend.likelyToUpgrade.answer = formData.likelyToUpgrade;

        // submit churn survey
        submitSurvey(finalDataToSend);
        formSubmit('Churn Survey', true, accountData);
      }

      // generic Plan Change modal
      onPlanChangeSubmit();
    },
    [submitSurvey, onPlanChangeSubmit, churnSurvey, accountData, formSubmit],
  );

  const onError = () => {
    formSubmit('Churn Survey', false, accountData, {
      app_form_submit_failure_reason: 'form invalid',
    });
    click('Continue', 'Change plan confirmation with churn survey modal', accountData);
  };

  const isDisabled: boolean = isSubmitting;

  const updateSurveyWithServerValues = useCallback(
    (questions: GetSurveyResponseQuestion[]) => {
      const [downgradeReason, downgradeExplanation, likelyToUpgrade] = questions;

      const updatedSurvey = getDefaultChurnSurvey(t);
      updatedSurvey.downgradeReason.question = downgradeReason.text;
      updatedSurvey.downgradeReason.uuid = downgradeReason.uuid;
      updatedSurvey.downgradeExplanation.question = downgradeExplanation.text;
      updatedSurvey.downgradeExplanation.uuid = downgradeExplanation.uuid;
      updatedSurvey.likelyToUpgrade.question = likelyToUpgrade.text;
      updatedSurvey.likelyToUpgrade.uuid = likelyToUpgrade.uuid;

      if (downgradeReason.answers) {
        downgradeReason.answers.forEach((answer: GetSurveyResponseAnswer, index: number) => {
          updatedSurvey.downgradeReason.options[index].text = answer.text;
        });
      }

      if (likelyToUpgrade.answers) {
        likelyToUpgrade.answers.forEach((answer: GetSurveyResponseAnswer, index: number) => {
          updatedSurvey.likelyToUpgrade.options[index].text = answer.text;
        });
      }

      // Shuffle dropdown answers, except the last one
      const lastArrayOption = updatedSurvey.downgradeReason.options.pop();
      updatedSurvey.downgradeReason.options = shuffle(updatedSurvey.downgradeReason.options);
      if (lastArrayOption) {
        updatedSurvey.downgradeReason.options.push(lastArrayOption);
      }

      setChurnSurvey(updatedSurvey);
    },
    [t],
  );

  useEffect(() => {
    if (surveyStatus === SurveyStatus.NOT_LOADING) {
      getSurvey();
    }
    if (surveyStatus === SurveyStatus.SUCCESS && surveyQuestions && surveyQuestions.length > 0) {
      updateSurveyWithServerValues(surveyQuestions);
    }
  }, [getSurvey, surveyStatus, surveyQuestions, updateSurveyWithServerValues]);

  return (
    <S.StyledBox style={style}>
      <form onSubmit={handleSubmit(onSubmit, onError)} id={formId}>
        {((surveyStatus === SurveyStatus.FETCHING || surveyStatus === SurveyStatus.NOT_LOADING) && (
          <Loader />
        )) || (
          <div data-testid="churn-survey-form">
            {churnSurvey && (
              <>
                <S.FormRow>
                  <S.Field
                    labelText={t('revenue:churn-survey.form.label-downgrade-reason')}
                    invalidText={
                      (!!errors.downgradeReason && errors.downgradeReason.message) ||
                      t('revenue:churn-survey.form.required-field')
                    }
                    inputId="downgrade-reason-select"
                    size="l"
                    isInvalid={!!errors.downgradeReason}
                    isRequired
                  >
                    <Select
                      id="downgrade-reason-select"
                      data-testid="downgrade-reason-select"
                      defaultValue=""
                      name="downgradeReason"
                      ref={register({
                        required: true,
                      })}
                      isInvalid={!!errors.downgradeReason}
                      isDisabled={isDisabled}
                      css={{ width: '100%', display: 'block' }}
                    >
                      <option value="" key="please-select-one">
                        {t('revenue:churn-survey.form.placeholder-downgrade-reason')}
                      </option>
                      {churnSurvey.downgradeReason.options.map((reason) => (
                        <option value={reason.text} key={`${reason.text}`}>
                          {reason.text}
                        </option>
                      ))}
                    </Select>
                  </S.Field>
                </S.FormRow>

                <S.FormRow>
                  <S.Field
                    labelText={t('revenue:churn-survey.form.label-downgrade-explanation')}
                    invalidText={
                      (!!errors.downgradeExplanation && errors.downgradeExplanation.message) ||
                      t('revenue:churn-survey.form.required-field')
                    }
                    inputId="downgrade-reason-long"
                    size="l"
                    isInvalid={!!errors.downgradeExplanation}
                  >
                    <Textarea
                      id="downgrade-reason-long"
                      data-testid="downgrade-reason-long"
                      name="downgradeExplanation"
                      placeholder={t('revenue:churn-survey.form.placeholder-downgrade-explanation')}
                      ref={register()}
                      isInvalid={!!errors.downgradeExplanation}
                      isDisabled={isDisabled}
                      maxLength={1500}
                      rows={4}
                    />
                  </S.Field>
                </S.FormRow>

                <S.FormRow>
                  <S.Field
                    labelText={t('revenue:churn-survey.form.label-likely-to-upgrade')}
                    inputId="likely-to-upgrade"
                    size="l"
                  >
                    <Controller
                      control={control}
                      name="likelyToUpgrade"
                      defaultValue=""
                      render={({ onChange, value }) => (
                        <S.RadioButtonContainer>
                          {churnSurvey.likelyToUpgrade.options.map((reason) => (
                            <S.RadioButton
                              key={reason.id}
                              name="likelyToUpgrade"
                              onChange={onChange}
                              value={reason.text}
                              labelContent={reason.text}
                              isChecked={value === reason.text}
                            />
                          ))}
                        </S.RadioButtonContainer>
                      )}
                    />
                  </S.Field>
                </S.FormRow>
              </>
            )}
            {!buttonIsOutsideOfForm && (
              <S.FormRow>
                <S.ButtonContainer>
                  <Button
                    type="submit"
                    isDisabled={isDisabled}
                    isLoading={isDisabled}
                    loadingText={t('revenue:churn-survey.form.continue-button-loading-text')}
                  >
                    {t('revenue:churn-survey.form.continue-button-continue')}
                  </Button>
                </S.ButtonContainer>
              </S.FormRow>
            )}
          </div>
        )}
      </form>
    </S.StyledBox>
  );
};
